import { PlusOutlined } from '@ant-design/icons';
import {
  type CreateLeadDistributionPayload,
  type DistributionRuleType,
  useCreateLeadDistributionMutation,
  useGetLeadDistributionQuery,
  useUpdateLeadDistributionMutation,
} from '@repo/crm-atoms';
import { message, notification, OwnerSelectDropdown } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { CaseUtil } from '@vectord/utils';
import {
  Alert, Button, Flex, Form,
} from 'antd';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import styles from './CreateLeadDistributionForm.module.scss';
import DistributionRuleUi from './DistributionRuleUi';

const initialRule: Partial<DistributionRuleType> = {
  name: '',
  entities: [],
};

export default function CreateLeadDistributionForm({
  onFinish,
  leadDistributionId,
}: {
  onFinish: (id?: number) => void;
  leadDistributionId?: number;
}) {
  const [distributionRules, setDistributionRules] = useState<Partial<DistributionRuleType>[]>([initialRule]);
  const [defaultUserUuid, setDefaultUserUuid] = useState<string | undefined>();

  const { data: leadDistributionData } = useGetLeadDistributionQuery(leadDistributionId!, {
    skip: !leadDistributionId,
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [createTrigger] = useCreateLeadDistributionMutation();
  const [updateTrigger] = useUpdateLeadDistributionMutation();

  useEffect(() => {
    if (leadDistributionData?.leadDistribution?.length) {
      const parsedRules = leadDistributionData?.leadDistribution[0]?.rules.map((rule) => ({
        ...rule,
        entities: rule.entities.map((entity) => ({
          entityUuid: entity?.entity?.userUuid,
        })),
        criteriaGroupId: Number(rule.criteriaGroupId),
      }));
      setDistributionRules(parsedRules ?? []);
      setDefaultUserUuid(leadDistributionData?.leadDistribution[0]?.defaultUser?.userUuid);
    }
  }, [leadDistributionData]);

  const validateRules = useMemo(() => distributionRules.every((rule) => (
    rule?.name?.trim().length
      && rule?.entities?.length
      && Number.isInteger(rule.criteriaGroupId)
  )), [distributionRules]);

  const handleAddRule = useCallback(() => {
    if (validateRules) {
      setDistributionRules([...distributionRules, initialRule]);
    } else {
      notification.error({
        message: 'Existing rule is not valid, Add all fields before adding a new rule',
        duration: 3,
        showProgress: true,
      });
    }
  }, [distributionRules, validateRules]);

  const handleChange = useCallback((index: number, rule: Partial<DistributionRuleType>) => {
    setDistributionRules((prev) => prev.map((r, i) => (i === index ? rule : r)));
  }, []);

  const handleDeleteRule = useCallback((index: number) => {
    setDistributionRules((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const saveLeadDistribution = useCallback(async () => {
    setLoading(true);
    if (!validateRules) return;

    const payload: CreateLeadDistributionPayload = {
      defaultUserUuid: defaultUserUuid || null,
      rules: distributionRules as DistributionRuleType[],
    };

    try {
      if (leadDistributionId) {
        const response = await updateTrigger({ id: leadDistributionId, body: CaseUtil.toCase('snakeCase', payload) });
        onFinish(response?.data?.lead_distribution?.id ?? 0);
      } else {
        const response = await createTrigger(CaseUtil.toCase('snakeCase', payload));
        onFinish(response?.data?.lead_distribution?.id ?? 0);
      }
    } catch (error: any) {
      message.error(error?.data?.message ?? translate(
        `Something went wrong while creating #{${TranslationEntity.lead}} distribution`,
      ));
    } finally {
      setLoading(false);
    }
  }, [createTrigger, defaultUserUuid, distributionRules, leadDistributionId, onFinish, updateTrigger, validateRules]);

  return (
    <Flex vertical gap={24} className={styles.container}>
      <Alert
        message={
          translate(
            `#{${TranslationEntity.lead}} entering will be distributed in a round robin manner`,
            [{ capitalize: true }],
          )
        }
        type="info"
        showIcon
        closable
      />
      <Flex vertical gap={24}>
        {distributionRules.map((rule, index) => (
          <DistributionRuleUi
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            rule={rule}
            onChange={(_rule) => handleChange(index, _rule)}
            onDelete={() => handleDeleteRule(index)}
            index={index}
          />
        ))}
      </Flex>
      <Button
        type="link"
        onClick={handleAddRule}
        icon={<PlusOutlined />}
        className={styles.addRuleBtn}
      >
        Add New Rule
      </Button>

      <Flex justify="space-between" className={styles.defaultRule}>
        <Flex wrap>
          <Form.Item
            label={
              translate(
                `Set Default Rule: When none of the above rules match the #{${TranslationEntity.lead}}, assign to:`,
                [{
                  capitalize: true,
                }],
              )
            }
            className={styles.defaultRuleLabel}
          />
          <OwnerSelectDropdown
            value={defaultUserUuid}
            className={styles.ownerSelect}
            handleClick={(value: string | string[]) => setDefaultUserUuid(value as string)}
          />
        </Flex>

        <Button
          className={styles.createBtn}
          type="primary"
          loading={loading}
          disabled={!validateRules}
          onClick={saveLeadDistribution}
        >
          {translate(`Create #{${TranslationEntity.lead}} Distribution`, [{
            capitalize: true,
          }])}
        </Button>
      </Flex>
    </Flex>
  );
}
